import {
  AbilityBuilder,
  type CreateAbility,
  createMongoAbility,
  type MongoAbility,
} from "@casl/ability"
import { z } from "zod/v4"

import type { TOrganizationRole } from "@/http/organizations/types"
import {
  apiKeySubject,
  financeAccountSubject,
  financeCategorySubject,
  memberSubject,
  organizationSubject,
  organizationUserSubject,
  peopleSubject,
} from "./subjects/organization"

/**
 * Organization-scoped abilities schema
 */
const organizationAbilitiesSchema = z.union([
  organizationSubject,
  memberSubject,
  peopleSubject,
  financeAccountSubject,
  financeCategorySubject,
  organizationUserSubject,
  apiKeySubject,
  z.tuple([z.literal("manage"), z.literal("all")]),
])

type TOrganizationAbilities = z.infer<typeof organizationAbilitiesSchema>

export type TOrganizationAbility = MongoAbility<TOrganizationAbilities>
export const createOrganizationAbility =
  createMongoAbility as CreateAbility<TOrganizationAbility>

/**
 * Define abilities based on organization role (matching backend permissions)
 * Admin can: manage all
 * Member can:
 *   - get, create, update People
 *   - get FinanceAccount
 *   - get FinanceCategory
 */
export function defineOrganizationAbilityFor(role: TOrganizationRole) {
  const builder = new AbilityBuilder(createOrganizationAbility)

  if (role === "admin") {
    // Admin has full access to everything
    builder.can("manage", "all")
  } else if (role === "member") {
    // Members have restricted access (matching backend)
    // People permissions
    builder.can("get", "People")
    builder.can("create", "People")
    builder.can("update", "People")

    // FinanceAccount permissions
    builder.can("get", "FinanceAccount")

    // FinanceCategory permissions
    builder.can("get", "FinanceCategory")
  }

  const ability = builder.build()

  ability.can = ability.can.bind(ability)
  ability.cannot = ability.cannot.bind(ability)

  return ability
}
